/**
 * Host Resource Monitoring
 *
 * Renders metric bars dynamically from the server's metrics array.
 * Each metric has a machine-readable name, human description, value, max, and unit.
 *
 * Supports both the local host and remote (delegated) hosts.  Each host gets
 * its own fieldset identified by a data-host-monitor attribute.  Remote host
 * metrics are fetched via proxied WebSocket connections to
 * /ws/monitor/host?host=<name>.
 */

import type { HostMetrics, HostMetric, HostAttribute } from '../api/types.js';
import { api } from '../api/client.js';
import { escapeHtml } from '../utils.js';
import { apiCall } from '../api/helpers.js';

// Module state — local host monitor
let resourceCallsDisabled = false;
let hostMonitorWs: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = 2000;
let requestInterval: ReturnType<typeof setInterval> | null = null;

const RECONNECT_DELAY_INITIAL = 2000;
const RECONNECT_DELAY_MAX = 30000;
const METRICS_INTERVAL = 2000;
const HOST_MONITOR_ATTR = 'data-host-monitor';

export function setResourceCallsDisabled(disabled: boolean): void {
  resourceCallsDisabled = disabled;
  if (disabled) {
    disconnectHostMonitor();
  }
}

/**
 * Format a metric value for display.
 * Rounds to 1 decimal place for fractional values.
 */
function formatValue(v: number): string {
  return v % 1 === 0 ? String(v) : v.toFixed(1);
}

/**
 * Render a single metric's info + bar elements.
 * Uses the metric name as a CSS class for targeting updates.
 */
function renderMetricHtml(metric: HostMetric): string {
  const cls = escapeHtml(metric.name);
  return `<div class="metric-info metric-info-${cls}"></div>` +
         `<div class="metric-bar metric-bar-${cls}"></div>`;
}

/**
 * Render a single attribute as a text line.
 */
function renderAttributeHtml(attr: HostAttribute): string {
  const cls = escapeHtml(attr.name);
  return `<div class="host-attr host-attr-${cls}"></div>`;
}

/** Names of the three load average metrics from the server */
const LOAD_NAMES = ['load_1min', 'load_5min', 'load_15min'] as const;

/** Check whether a metric name is one of the load averages */
function isLoadMetric(name: string): boolean {
  return (LOAD_NAMES as readonly string[]).includes(name);
}

/**
 * Render the combined load average bar HTML.
 */
function renderLoadBarHtml(): string {
  return `<div class="metric-info metric-info-load_avg"></div>` +
         `<div class="load-bar metric-bar-load_avg">` +
           `<div class="load-bar-layer load-bar-15min"></div>` +
           `<div class="load-bar-layer load-bar-5min"></div>` +
           `<div class="load-bar-layer load-bar-1min"></div>` +
         `</div>`;
}

/**
 * Update the resource bars for a specific host.
 * Creates the container and bars on first call; updates in place on subsequent calls.
 *
 * @param data - Metrics payload from the WebSocket
 * @param hostKey - Unique key for this host ("" for local, host name for remote)
 * @param legendText - Legend text for the fieldset
 *
 * The three load_* metrics are consolidated into a single 3-layer stacked bar
 * instead of rendering three separate progress bars.
 */
function updateMetrics(data: HostMetrics, hostKey: string, legendText: string): void {
  const $area = $('#resource-area');
  const selector = `[${HOST_MONITOR_ATTR}="${escapeHtml(hostKey)}"]`;
  let $container = $area.find(selector);

  // Separate load metrics from regular metrics
  const regularMetrics = data.metrics.filter(m => !isLoadMetric(m.name));
  const loadMetrics: Record<string, HostMetric> = {};
  for (const m of data.metrics) {
    if (isLoadMetric(m.name)) loadMetrics[m.name] = m;
  }
  const hasLoad = Object.keys(loadMetrics).length > 0;

  // Create container on first update
  if ($container.length === 0) {
    const barsHtml = regularMetrics.map(renderMetricHtml).join('');
    const loadHtml = hasLoad ? renderLoadBarHtml() : '';
    const attrsHtml = (data.attributes || []).map(renderAttributeHtml).join('');
    $area.append(`
      <fieldset class="host-resources" ${HOST_MONITOR_ATTR}="${escapeHtml(hostKey)}">
        <legend>${escapeHtml(legendText)}</legend>
        <div class="host-attributes">${attrsHtml}</div>
        ${barsHtml}
        ${loadHtml}
      </fieldset>
      <br/>
    `);
    $container = $area.find(selector);

    // Initialize jQuery UI progressbars for regular metrics
    for (const m of regularMetrics) {
      $container.find(`.metric-bar-${m.name}`).progressbar();
    }
  }

  // Update attributes
  const $attrs = $container.find('.host-attributes');
  for (const a of (data.attributes || [])) {
    let $attr = $attrs.find(`.host-attr-${a.name}`);
    if ($attr.length === 0) {
      $attrs.append(renderAttributeHtml(a));
      $attr = $attrs.find(`.host-attr-${a.name}`);
    }
    $attr.html(`<span class='caption'>${escapeHtml(a.description)}: ${escapeHtml(a.value)}</span>`);
  }

  // Update regular metrics
  for (const m of regularMetrics) {
    let $bar = $container.find(`.metric-bar-${m.name}`);
    if ($bar.length === 0) {
      $container.append(renderMetricHtml(m));
      $bar = $container.find(`.metric-bar-${m.name}`);
      $bar.progressbar();
    }

    const percent = m.max > 0 ? (m.value / m.max) * 100 : 0;
    $bar.progressbar('option', 'value', percent);

    const caption = m.unit
      ? `${formatValue(m.value)} / ${formatValue(m.max)} ${m.unit}`
      : `${formatValue(m.value)} / ${formatValue(m.max)}`;
    $container
      .find(`.metric-info-${m.name}`)
      .html(`<span class='caption'>${escapeHtml(m.description)}: ${caption}</span>`);
  }

  // Update combined load bar
  if (hasLoad) {
    // Ensure load bar exists
    if ($container.find('.metric-bar-load_avg').length === 0) {
      $container.append(renderLoadBarHtml());
    }

    const load1 = loadMetrics.load_1min;
    const load5 = loadMetrics.load_5min;
    const load15 = loadMetrics.load_15min;
    const max = load1?.max || load5?.max || load15?.max || 1;

    // Update each layer's width
    if (load15) $container.find('.load-bar-15min').css('width', `${(load15.value / max) * 100}%`);
    if (load5)  $container.find('.load-bar-5min').css('width', `${(load5.value / max) * 100}%`);
    if (load1)  $container.find('.load-bar-1min').css('width', `${(load1.value / max) * 100}%`);

    // Caption: "Load Average: 0.0 / 0.0 / 0.1 (2 cores)"
    const v1 = load1 ? formatValue(load1.value) : '-';
    const v5 = load5 ? formatValue(load5.value) : '-';
    const v15 = load15 ? formatValue(load15.value) : '-';
    $container
      .find('.metric-info-load_avg')
      .html(`<span class='caption'>Load Average: ${v1} / ${v5} / ${v15} (${formatValue(max)} cores)</span>`);
  }
}

/**
 * Connect to the host monitor WebSocket for real-time resource updates.
 */
export function connectHostMonitor(): void {
  if (resourceCallsDisabled) return;
  if (hostMonitorWs) return; // Already connected

  const ws = new WebSocket(api.buildWsUrl('/ws/monitor/host'));
  hostMonitorWs = ws;
  let opened = false;

  ws.onopen = () => {
    opened = true;
    reconnectDelay = RECONNECT_DELAY_INITIAL;
    // Start requesting metrics on an interval
    requestInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'get_metrics' }));
      }
    }, METRICS_INTERVAL);
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'host_metrics') {
        updateMetrics(data as HostMetrics, '', 'Host');
      }
    } catch (e) {
      console.error('Failed to parse host metrics message:', e);
    }
  };

  ws.onclose = () => {
    cleanup();
    if (!opened) {
      // WS upgrade failed — verify session before retrying
      api.verifySession().then(valid => {
        if (valid) scheduleReconnect();
      });
    } else {
      scheduleReconnect();
    }
  };

  ws.onerror = () => {
    // onclose will fire after onerror, which handles cleanup/reconnect
  };
}

/**
 * Disconnect the host monitor WebSocket and all remote host monitors.
 */
export function disconnectHostMonitor(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  cleanup();
  disconnectRemoteHostMonitors();
}

function cleanup(): void {
  if (requestInterval) {
    clearInterval(requestInterval);
    requestInterval = null;
  }
  if (hostMonitorWs) {
    const ws = hostMonitorWs;
    hostMonitorWs = null;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close();
    }
  }
}

function scheduleReconnect(): void {
  if (resourceCallsDisabled) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connectHostMonitor();
  }, reconnectDelay);
  reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_DELAY_MAX);
}

// ============================================================================
// Remote Host Resource Monitors
// ============================================================================

interface RemoteMonitorConn {
  host: string;
  ws: WebSocket | null;
  interval: ReturnType<typeof setInterval> | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  reconnectDelay: number;
}

const remoteMonitors: RemoteMonitorConn[] = [];

/**
 * Open a proxied host-monitor WebSocket for each remote host.
 * Each remote host gets its own resource fieldset with identical graphs
 * to the local host.
 *
 * @param hostNames - Explicit list of remote host names to monitor.
 *   If omitted, fetches ALL configured remote hosts from the API.
 *   Pass an explicit list to restrict monitoring to hosts that are
 *   relevant (e.g. only hosts with VMs in the current organization).
 */
export async function connectRemoteHostMonitors(hostNames?: string[]): Promise<void> {
  if (resourceCallsDisabled) return;

  let names: string[];
  if (hostNames) {
    names = hostNames;
  } else {
    const response = await apiCall(api.listHosts(), 'hosts');
    if (!response || response.hosts.length === 0) return;
    names = response.hosts.map(h => h.name);
  }

  for (const name of names) {
    const rm: RemoteMonitorConn = {
      host: name,
      ws: null,
      interval: null,
      reconnectTimer: null,
      reconnectDelay: RECONNECT_DELAY_INITIAL,
    };
    remoteMonitors.push(rm);
    connectRemoteMonitor(rm);
  }
}

function connectRemoteMonitor(rm: RemoteMonitorConn): void {
  if (resourceCallsDisabled || rm.ws) return;

  const wsUrl = api.buildWsUrl(
    `/ws/monitor/host?host=${encodeURIComponent(rm.host)}`
  );
  const socket = new WebSocket(wsUrl);
  rm.ws = socket;
  let opened = false;

  socket.onopen = () => {
    opened = true;
    rm.reconnectDelay = RECONNECT_DELAY_INITIAL;
    // Poll for metrics on the same interval as the local monitor
    rm.interval = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'get_metrics' }));
      }
    }, METRICS_INTERVAL);
  };

  socket.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'host_metrics') {
        updateMetrics(data as HostMetrics, rm.host, rm.host);
      }
    } catch {
      // Ignore parse errors
    }
  };

  socket.onclose = () => {
    if (rm.interval) {
      clearInterval(rm.interval);
      rm.interval = null;
    }
    rm.ws = null;
    if (!resourceCallsDisabled) {
      if (!opened) {
        // WS upgrade failed — verify session before retrying
        api.verifySession().then(valid => {
          if (valid) {
            rm.reconnectTimer = setTimeout(() => {
              rm.reconnectTimer = null;
              connectRemoteMonitor(rm);
            }, rm.reconnectDelay);
            rm.reconnectDelay = Math.min(rm.reconnectDelay * 2, RECONNECT_DELAY_MAX);
          }
        });
      } else {
        rm.reconnectTimer = setTimeout(() => {
          rm.reconnectTimer = null;
          connectRemoteMonitor(rm);
        }, rm.reconnectDelay);
        rm.reconnectDelay = Math.min(rm.reconnectDelay * 2, RECONNECT_DELAY_MAX);
      }
    }
  };

  socket.onerror = () => {
    // onclose fires after onerror
  };
}

function disconnectRemoteHostMonitors(): void {
  for (const rm of remoteMonitors) {
    if (rm.reconnectTimer) {
      clearTimeout(rm.reconnectTimer);
      rm.reconnectTimer = null;
    }
    if (rm.interval) {
      clearInterval(rm.interval);
      rm.interval = null;
    }
    if (rm.ws) {
      const old = rm.ws;
      rm.ws = null;
      if (old.readyState === WebSocket.OPEN || old.readyState === WebSocket.CONNECTING) {
        old.close();
      }
    }
  }
  remoteMonitors.length = 0;
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).connectHostMonitor = connectHostMonitor;
  (window as unknown as Record<string, unknown>).disconnectHostMonitor = disconnectHostMonitor;
}
